import { useEffect, useMemo, useRef } from "react"
import { useOptimization } from "hooks/optimizer"
import { SelectedTypedInstrument } from "helpers/types/instruments/selected"
import { IsoDate } from "helpers/types/dates"
import { OptimizedPositionSet, OptimizationConfiguration } from "./types"
import { usePortfolioHoldings } from "hooks/rebalancer"
import { calculateCurrentMarketValues, getOptimizationResultName } from "../components/helpers/configuration"
import { ERROR, OK } from "helpers/data-statuses"
import { useIntradayMode } from "connected-components/dashboard/hooks/use-intraday-mode"

export const useAutoRunOptimization = (
  instrument: SelectedTypedInstrument | null,
  modelId: string,
  endDate: IsoDate,
  base: SelectedTypedInstrument | null,
  setOptimizeConfiguration: (configuration: OptimizationConfiguration) => void,
  optimizeConfiguration: OptimizationConfiguration | null,
  setComparisonInstrument: (instrument: OptimizedPositionSet | null) => void,
  isOptimizeExperimentsEnabled: boolean,
  isOptimizePanelExpanded: boolean,
): void => {
  const [portfolioHoldings, portfolioHoldingsStatus] = usePortfolioHoldings(instrument, modelId, endDate)
  const portfolioMarketValues = useMemo(() => {
    if (portfolioHoldingsStatus !== OK) {
      return { longMarketValue: 0, shortMarketValue: 0 }
    }
    return calculateCurrentMarketValues(portfolioHoldings)
  }, [portfolioHoldings, portfolioHoldingsStatus])
  const forecastDataRef = useRef<string | null>(null)
  const forecastDataLoadingRef = useRef<boolean>(false)
  const forecast = optimizeConfiguration?.forecast && optimizeConfiguration.forecast !== "none"
    ? optimizeConfiguration.forecast : null

  const { runOptimization, getConfiguration, optimizedPositionSet, getForecastData, forecastDataStatus, forecastData } = useOptimization(
    instrument,
    modelId,
    endDate,
    base,
    { overrideConfiguration: optimizeConfiguration },
    portfolioMarketValues,
  )

  useEffect(() => {
    forecastDataRef.current = null
  }, [forecast])

  const [{ isIntradayMode }] = useIntradayMode()
  useEffect(() => {
    const canRunOptimization = Boolean(
      isOptimizeExperimentsEnabled
      && optimizeConfiguration
      && endDate
      && instrument
      && modelId
      && portfolioHoldingsStatus === OK
      && !isIntradayMode,
    )

    if (!canRunOptimization) {
      return
    }

    if (optimizeConfiguration?.on === endDate || isOptimizePanelExpanded) {
      return
    }

    if (forecast && forecastDataRef.current === null) {
      forecastDataRef.current = forecast
      forecastDataLoadingRef.current = true
      getForecastData()
      return
    }

    if (forecastDataLoadingRef.current) {
      return
    }

    runOptimization()
    const configuration = getConfiguration()
    setOptimizeConfiguration(configuration)
  }, [
    runOptimization,
    setOptimizeConfiguration,
    endDate,
    optimizeConfiguration,
    getConfiguration,
    instrument,
    modelId,
    base,
    isOptimizeExperimentsEnabled,
    isOptimizePanelExpanded,
    portfolioHoldingsStatus,
    forecast,
    forecastDataStatus,
    getForecastData,
    isIntradayMode,
  ])

  useEffect(() => {
    if (isOptimizePanelExpanded) {
      return
    }

    if (forecastDataLoadingRef.current && forecastData && forecastDataStatus === OK) {
      forecastDataLoadingRef.current = false
      runOptimization()
      const configuration = getConfiguration()
      setOptimizeConfiguration(configuration)
    } else if (forecastDataStatus === ERROR) {
      forecastDataLoadingRef.current = false
    }
  }, [forecastData, forecastDataStatus, runOptimization, getConfiguration, setOptimizeConfiguration, isOptimizePanelExpanded])

  useEffect(() => {
    if (!isOptimizeExperimentsEnabled || !optimizedPositionSet || isIntradayMode) {
      return
    }
    const positionSetWithName = {
      ...optimizedPositionSet,
      name: getOptimizationResultName(instrument?.type),
    }
    setComparisonInstrument(positionSetWithName)
  }, [optimizedPositionSet, setComparisonInstrument, isOptimizeExperimentsEnabled, instrument?.type, isIntradayMode])
}
